// §4.3 — PRICE LIST DETAIL, /sales/price-lists/$priceListId (Module 09,
// P3-A part 2). The grid over GET /v1/price-lists/{id} — the part-1 added
// detail read (its §5 table row is a spec write-back shipped with this PR).
//
// THE LIFECYCLE, on the glass: only DRAFTS edit — an ACTIVE list renders
// read-only with the "new draft to reprice" note, so the 409
// PRICE_LIST_NOT_DRAFT refusal is never reachable through an offered control.
// Activation runs the (tenant, tier, currency) overlap check server-side; a
// 409 VALIDITY_OVERLAP carries `conflicting_price_list_id` in the problem
// extensions and renders as the INLINE CONFLICT PANEL (§4.3's stated error
// state) — the conflicting list fetched and shown beside this one's dates so
// the admin can see exactly which validity ranges collide.
//
// THE GRID saves WHOLESALE: one PUT replaces the item set (rows absent from
// the payload are deleted — the screen states that above the save control).
// The margin-preview column renders IFF ≥ 1 item carries `margin_preview_pct`
// (§7 omission redaction, lib/money.ts `columnPresent`); an absent cell in a
// rendered column renders NOTHING — no column of locks, no dash.
//
// THE IMPORT LANE is client-parsed CSV/TSV + paste (lib/price-import.ts;
// §4.3's "Excel import" sentence is amended in the same PR — .xlsx parsing
// needs a dependency no DECISIONS entry admits yet, and TSV paste IS what
// Excel's clipboard emits). SKUs resolve through the catalogue search, UoM
// codes through the UoM catalog, prices convert by exact string math, then
// ONE PUT — and the server's row-validation report renders in the module-02
// grammar (applied/deleted counts + per-row rejections with localized codes),
// merged with the client's own pre-check rejections, labelled by line.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { RefLabel } from "../../../../components/ref-label";
import { Select } from "../../../../components/select";
import {
  CardSkeleton,
  ErrorState,
  ForbiddenState,
  OfflineState,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { formatBusinessDate } from "../../../../lib/datetime";
import { client } from "../../../../lib/api";
import { useUoms } from "../../catalogue/hooks/catalogue-queries";
import { useProductIndex, useProductSearch } from "../../production/hooks/production-queries";
import {
  columnPresent,
  formatMinor,
  minorToDecimal,
  minorUnitDigits,
  normalizeTypedDecimal,
} from "../lib/money";
import {
  assemblePutItems,
  decimalToMinor,
  parsePriceTable,
  rowCodeLabel,
} from "../lib/price-import";
import { MutationFailureNote } from "../mutation-failure-note";
import { isSalesReadRefused, useSalesPermissions } from "../hooks/permissions";
import { isSalesValueRefusal } from "../lib/value-order";
import {
  useActivatePriceList,
  useArchivePriceList,
  usePriceList,
  usePutPriceListItems,
  type PriceListItemsReport,
} from "../hooks/sales-queries";
import { priceListStatusVariant } from "./price-lists";
import { bookClientPager } from "../hooks/book-pages";

/** One editable grid row. `itemId` keys server-loaded rows; added rows mint a
 * local key. The PUT payload is assembled from these wholesale. */
interface GridRow {
  readonly key: string;
  readonly productId: string;
  readonly uomId: string;
  readonly minQty: string;
  /** Decimal MAJOR text — converted by exact string math on save. */
  readonly priceText: string;
  /** Present on rows that came from the server (for the margin column). */
  readonly marginPreviewPct: string | undefined;
}

/** The merged report row: server rejections carry the payload index, client
 * pre-check rejections the file line. */
interface ReportRow {
  readonly key: string;
  readonly label: string;
  readonly code: string;
}

export function SalesPriceListDetailScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();
  const params = useParams({ strict: false });
  const priceListId = typeof params.priceListId === "string" ? params.priceListId : "";

  const refused = isSalesReadRefused(perms, perms.canOpenPricing && perms.has("sales.read"));
  const detail = usePriceList(refused ? "" : priceListId);
  const canManage = perms.has("sales.tier.manage");

  const uoms = useUoms();
  const activate = useActivatePriceList();
  const archive = useArchivePriceList();
  const putItems = usePutPriceListItems();

  const [rows, setRows] = useState<readonly GridRow[]>([]);
  const [dirty, setDirty] = useState(false);
  const [report, setReport] = useState<
    { applied: number; deleted: number; rows: readonly ReportRow[] } | undefined
  >(undefined);

  const list = detail.data?.price_list;
  const digits = minorUnitDigits(list?.currency ?? "USD", i18n.language);

  // Seed the grid from the server's item set; re-seed on a fresh payload
  // unless local edits are pending (a wholesale PUT refetch resets `dirty`).
  const items = detail.data?.items;
  useEffect(() => {
    if (items === undefined || dirty) return;
    setRows(
      items.map((item) => ({
        key: item.id,
        productId: item.product_id,
        uomId: item.uom_id,
        minQty: item.min_qty,
        priceText: minorToDecimal(item.unit_price_minor, digits),
        marginPreviewPct: item.margin_preview_pct,
      })),
    );
  }, [items, dirty, digits]);

  useEffect(() => {
    if (detail.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [detail.error, navigate]);

  const products = useProductIndex(rows.map((row) => row.productId));
  const uomCode = (id: string): string | undefined =>
    (uoms.data?.items ?? []).find((u) => u.id === id)?.code;

  // The §4.3 conflict: read the typed extension off the 409's problem body.
  const conflictId =
    activate.error instanceof RequestError && activate.error.code === "VALIDITY_OVERLAP"
      ? (((activate.error.problem ?? {}) as Record<string, unknown>)[
          "conflicting_price_list_id"
        ] as string | undefined)
      : undefined;

  if (refused) {
    return <ForbiddenState title={t("sales.access.title")} body={t("sales.access.pricing")} />;
  }
  if (detail.fetchStatus === "paused" && detail.data === undefined) {
    return <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />;
  }
  if (detail.isPending) {
    return <CardSkeleton lines={8} label={t("sales.common.loading")} />;
  }
  // D-301: the items ARE prices, so the server refuses them to a reader
  // without `sales.read_value`. The screen's audience (group_admin,
  // facility_admin, sales) holds the grant in the role mirror, so no reader
  // the mirror knows asks; this is the stated answer when the mirror is wrong
  // about a role, never an error card.
  if (isSalesValueRefusal(detail.error)) {
    return <ForbiddenState title={t("sales.access.title")} body={t("sales.access.pricingValue")} />;
  }
  if (detail.error !== null || list === undefined) {
    return (
      <ErrorState
        title={t("sales.priceListDetail.error")}
        retryLabel={t("sales.common.retry")}
        requestIdLabel={t("sales.common.requestId")}
        requestId={requestIdOf(detail.error)}
        onRetry={() => {
          void detail.refetch();
        }}
      />
    );
  }

  const isDraft = list.status === "draft";
  const editable = isDraft && canManage;
  // §7 omission: the column exists iff at least one SERVER row carried the key
  // (a locally-added row has no preview until the next read).
  const showMargin = columnPresent(rows, (row) => row.marginPreviewPct);

  const columns: DataTableColumn<GridRow>[] = [
    {
      key: "productId",
      header: t("sales.priceListDetail.colProduct"),
      id: true,
      render: (row) => {
        const ref = products.byId.get(row.productId);
        return (
          <RefLabel
            value={ref !== undefined ? `${ref.sku} · ${ref.name}` : undefined}
            id={row.productId}
            pending={products.isPending}
          />
        );
      },
    },
    {
      key: "uomId",
      header: t("sales.priceListDetail.colUom"),
      render: (row) => (
        <RefLabel value={uomCode(row.uomId)} id={row.uomId} pending={uoms.isPending} />
      ),
    },
    {
      key: "minQty",
      header: t("sales.priceListDetail.colMinQty"),
      numeric: true,
      render: (row) => <span className="num">{row.minQty}</span>,
    },
    {
      key: "priceText",
      header: t("sales.priceListDetail.colPrice"),
      numeric: true,
      render: (row) => {
        const minor = decimalToMinor(row.priceText, digits);
        return (
          <span className="num">
            {minor === undefined ? row.priceText : formatMinor(minor, list.currency, i18n.language)}
          </span>
        );
      },
    },
    ...(showMargin
      ? [
          {
            key: "marginPreviewPct",
            header: t("sales.priceListDetail.colMargin"),
            numeric: true,
            render: (row: GridRow) =>
              // A rendered column's ABSENT cell renders NOTHING (§7 omission).
              row.marginPreviewPct === undefined ? null : (
                <span className="num" data-testid="sales-margin-preview">
                  {t("sales.priceListDetail.marginPct", { pct: row.marginPreviewPct })}
                </span>
              ),
          } satisfies DataTableColumn<GridRow>,
        ]
      : []),
    ...(editable
      ? [
          {
            key: "actions",
            header: t("sales.priceListDetail.colActions"),
            actions: true,
            render: (row: GridRow) => (
              <Button
                size="sm"
                variant="danger"
                data-testid="sales-grid-row-remove"
                onClick={() => {
                  setRows((prior) => prior.filter((r) => r.key !== row.key));
                  setDirty(true);
                }}
              >
                {t("sales.priceListDetail.removeRow")}
              </Button>
            ),
          } satisfies DataTableColumn<GridRow>,
        ]
      : []),
  ];

  return (
    <section className="sales-price-list-detail" data-testid="sales-price-list-detail">
      <PageHeader
        title={list.code}
        crumbs={[{ label: t("sales.priceListDetail.crumbRoot"), to: "/sales/price-lists" }]}
        actions={
          canManage ? (
            <>
              {isDraft ? (
                <Button
                  variant="primary"
                  size="sm"
                  data-testid="sales-price-list-activate"
                  busy={activate.isPending}
                  busyLabel={t("sales.priceListDetail.activating")}
                  onClick={() => {
                    archive.reset();
                    activate.mutate(list.id);
                  }}
                >
                  {t("sales.priceListDetail.activate")}
                </Button>
              ) : null}
              {list.status !== "archived" ? (
                <Button
                  variant="danger"
                  size="sm"
                  data-testid="sales-price-list-archive"
                  busy={archive.isPending}
                  busyLabel={t("sales.common.saving")}
                  onClick={() => {
                    activate.reset();
                    archive.mutate(list.id);
                  }}
                >
                  {t("sales.priceListDetail.archive")}
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      />

      <div className="sales-360-header">
        <div className="sales-360-idline">
          <span>{list.name}</span>
          <StatusChip variant="neutral" dot={false}>
            {t(`sales.tier.${list.tier}`)}
          </StatusChip>
          <span className="num">{list.currency}</span>
          <StatusChip variant="neutral" dot={false} data-testid="sales-vat-chip">
            {t(`sales.priceLists.vat.${list.vat_mode}`)}
          </StatusChip>
          <span className="num">
            {formatBusinessDate(list.valid_from, i18n.language)} –{" "}
            {list.valid_to === null
              ? t("sales.priceLists.openEnded")
              : formatBusinessDate(list.valid_to, i18n.language)}
          </span>
          <StatusChip
            variant={priceListStatusVariant(list.status)}
            data-testid="sales-price-list-status"
          >
            {t(`sales.priceLists.status.${list.status}`)}
          </StatusChip>
        </div>
      </div>

      {activate.isError && conflictId === undefined ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.priceListDetail.activateFailed")}
          error={activate.error}
        />
      ) : null}
      {archive.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.priceListDetail.archiveFailed")}
          error={archive.error}
        />
      ) : null}

      {conflictId !== undefined ? (
        <ConflictPanel conflictId={conflictId} own={list.valid_from} ownTo={list.valid_to} />
      ) : null}

      {!isDraft ? (
        <p className="sales-hint" data-testid="sales-grid-readonly-note">
          {t("sales.priceListDetail.readOnlyNote")}
        </p>
      ) : null}

      {rows.length === 0 ? (
        <p className="sales-hint" data-testid="sales-grid-empty">
          {t("sales.priceListDetail.emptyItems")}
        </p>
      ) : (
        <DataTable
          clientPager={bookClientPager(t, i18n.language, { appendable: true })}
          data-testid="sales-price-list-grid"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.key}
          empty={null}
          caption={t("sales.priceListDetail.tableCaption")}
          rowTestId="sales-price-list-item-row"
        />
      )}

      {editable ? (
        <DraftEditor
          digits={digits}
          onAdd={(row) => {
            setRows((prior) => [...prior, row]);
            setDirty(true);
          }}
        />
      ) : null}

      {editable ? (
        <div className="sales-toolbar">
          {/* The PUT replaces the item set wholesale — said, not implied. */}
          <p className="sales-hint">{t("sales.priceListDetail.wholesaleNote")}</p>
          <Button
            variant="primary"
            data-testid="sales-grid-save"
            busy={putItems.isPending}
            busyLabel={t("sales.common.saving")}
            disabled={
              rows.length === 0 ||
              rows.some((row) => decimalToMinor(row.priceText, digits) === undefined)
            }
            onClick={() => {
              const payload: {
                product_id: string;
                uom_id: string;
                min_qty: string;
                unit_price_minor: number;
              }[] = [];
              for (const row of rows) {
                const minor = decimalToMinor(row.priceText, digits);
                // The disabled guard above makes this unreachable; a row that
                // somehow slipped through is DROPPED, never sent as a zero.
                if (minor === undefined) continue;
                payload.push({
                  product_id: row.productId,
                  uom_id: row.uomId,
                  min_qty: row.minQty,
                  unit_price_minor: minor,
                });
              }
              putItems.mutate(
                { id: list.id, body: { items: payload } },
                {
                  onSuccess: (serverReport) => {
                    setDirty(false);
                    setReport({
                      applied: serverReport.applied,
                      deleted: serverReport.deleted,
                      rows: serverRows(serverReport),
                    });
                  },
                },
              );
            }}
          >
            {t("sales.priceListDetail.save")}
          </Button>
        </div>
      ) : null}

      {putItems.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.priceListDetail.saveFailed")}
          error={putItems.error}
        />
      ) : null}

      {editable ? (
        <ImportPanel
          listId={list.id}
          digits={digits}
          onReport={(merged) => {
            setDirty(false);
            setReport(merged);
          }}
        />
      ) : null}

      {report !== undefined ? <ReportCard report={report} /> : null}
    </section>
  );
}

/** Payload-index labels for the server's rejected rows. */
function serverRows(report: PriceListItemsReport): ReportRow[] {
  return report.rejected_rows.map((row) => ({
    key: `srv-${row.index}`,
    label: `#${row.index + 1}`,
    code: row.code,
  }));
}

/** The module-02 row-report grammar: counts, then per-row rejections. */
function ReportCard({
  report,
}: {
  report: { applied: number; deleted: number; rows: readonly ReportRow[] };
}) {
  const { t } = useTranslation();
  return (
    <Card
      className="sales-report"
      data-testid="price-list-import-report"
      title={t("sales.priceListDetail.reportTitle")}
    >
      <p className="num">
        {t("sales.priceListDetail.reportApplied", { count: report.applied })} ·{" "}
        {t("sales.priceListDetail.reportDeleted", { count: report.deleted })}
      </p>
      {report.rows.length > 0 ? (
        <ul className="sales-report-rows">
          {report.rows.map((row) => (
            <li key={row.key} data-testid="price-list-report-row" data-code={row.code}>
              <span className="num">{row.label}</span> {rowCodeLabel(t, row.code)}
            </li>
          ))}
        </ul>
      ) : (
        <p className="sales-hint">{t("sales.priceListDetail.reportClean")}</p>
      )}
    </Card>
  );
}

/** The exact-SKU add-row form (the remnants itemMatch idiom): a row arms only
 * on an exact catalogue match and a convertible price. */
function DraftEditor({ digits, onAdd }: { digits: number; onAdd: (row: GridRow) => void }) {
  const { t, i18n } = useTranslation();
  const uoms = useUoms();
  const [skuText, setSkuText] = useState("");
  const [uomId, setUomId] = useState("");
  const [minQty, setMinQty] = useState("0");
  const [priceText, setPriceText] = useState("");
  const search = useProductSearch(skuText.trim());
  const match = useMemo(() => {
    const needle = skuText.trim().toLowerCase();
    if (needle.length === 0) return undefined;
    return (search.data?.items ?? []).find((p) => p.sku.toLowerCase() === needle);
  }, [search.data, skuText]);

  const effectiveUomId = uomId.length > 0 ? uomId : (match?.base_uom_id ?? "");
  // TYPED text is normalised from the ACTIVE LOCALE before parsing (#204), so
  // «45 000,00» from a ru keyboard is the price the screen just printed. The
  // CSV rows above are deliberately NOT normalised: a pasted file may be in any
  // locale, and guessing per cell is how an import multiplies a column by a
  // thousand. Normalising HERE — where typed text becomes a row — means
  // everything downstream sees one canonical form.
  const typedPrice = normalizeTypedDecimal(priceText, i18n.language);
  const priceOk = decimalToMinor(typedPrice, digits) !== undefined;
  const minQtyOk = /^\d{1,11}(\.\d{1,3})?$/u.test(minQty.trim());
  const seq = useRef(0);

  return (
    <div className="filter-bar sales-grid-add" data-testid="sales-grid-add">
      <Field label={t("sales.priceListDetail.addSku")} wide>
        <input
          value={skuText}
          data-testid="sales-grid-add-sku"
          onChange={(e) => {
            setSkuText(e.target.value);
          }}
        />
        {skuText.trim().length > 0 ? (
          match !== undefined ? (
            <span className="sales-applied">
              {t("sales.priceListDetail.skuApplied", { sku: match.sku, name: match.name })}
            </span>
          ) : (
            <span className="sales-applied">{t("sales.priceListDetail.skuNoMatch")}</span>
          )
        ) : null}
      </Field>
      <Field label={t("sales.priceListDetail.addUom")}>
        <Select
          value={effectiveUomId}
          data-testid="sales-grid-add-uom"
          onChange={(e) => {
            setUomId(e.target.value);
          }}
        >
          {effectiveUomId.length === 0 ? (
            <option value="">{t("sales.priceCheck.uomPick")}</option>
          ) : null}
          {(uoms.data?.items ?? []).map((uom) => (
            <option key={uom.id} value={uom.id}>
              {uom.code}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t("sales.priceListDetail.addMinQty")}>
        <input
          inputMode="decimal"
          value={minQty}
          data-testid="sales-grid-add-min-qty"
          onChange={(e) => {
            setMinQty(e.target.value);
          }}
        />
      </Field>
      <Field label={t("sales.priceListDetail.addPrice")}>
        <input
          inputMode="decimal"
          value={priceText}
          data-testid="sales-grid-add-price"
          onChange={(e) => {
            setPriceText(e.target.value);
          }}
        />
      </Field>
      <Button
        size="sm"
        data-testid="sales-grid-add-confirm"
        disabled={match === undefined || effectiveUomId.length === 0 || !priceOk || !minQtyOk}
        onClick={() => {
          if (match === undefined) return;
          seq.current += 1;
          onAdd({
            key: `new-${seq.current}-${match.id}`,
            productId: match.id,
            uomId: effectiveUomId,
            minQty: minQty.trim(),
            priceText: typedPrice,
            marginPreviewPct: undefined,
          });
          setSkuText("");
          setPriceText("");
          setMinQty("0");
        }}
      >
        {t("sales.priceListDetail.addRow")}
      </Button>
    </div>
  );
}

/** CSV/TSV file-or-paste import: parse client-side, resolve, ONE PUT. */
function ImportPanel({
  listId,
  digits,
  onReport,
}: {
  listId: string;
  digits: number;
  onReport: (report: { applied: number; deleted: number; rows: readonly ReportRow[] }) => void;
}) {
  const { t } = useTranslation();
  const uoms = useUoms();
  const putItems = usePutPriceListItems();
  const [pasted, setPasted] = useState("");
  const [running, setRunning] = useState(false);
  const [note, setNote] = useState<string | undefined>(undefined);

  const run = async (text: string): Promise<void> => {
    setNote(undefined);
    const parsed = parsePriceTable(text);
    if (parsed.headerError) {
      setNote(t("sales.import.badHeader"));
      return;
    }
    if (parsed.rows.length === 0 && parsed.errors.length === 0) {
      setNote(t("sales.import.emptyFile"));
      return;
    }
    setRunning(true);
    try {
      // Exact-SKU resolution through the catalogue search — one query per
      // UNIQUE SKU, in small parallel batches.
      const uniqueSkus = [...new Set(parsed.rows.map((row) => row.sku.toLowerCase()))];
      const productBySku = new Map<string, string>();
      const batch = 8;
      for (let i = 0; i < uniqueSkus.length; i += batch) {
        await Promise.all(
          uniqueSkus.slice(i, i + batch).map(async (sku) => {
            const res = await client.GET("/v1/catalogue/products", {
              params: { query: { q: sku } },
            });
            const hit = (res.data?.items ?? []).find((p) => p.sku.toLowerCase() === sku);
            if (hit !== undefined) productBySku.set(sku, hit.id);
          }),
        );
      }
      const uomByCode = new Map<string, string>();
      for (const uom of uoms.data?.items ?? []) uomByCode.set(uom.code.toLowerCase(), uom.id);

      const assembled = assemblePutItems(parsed.rows, productBySku, uomByCode, digits);
      const clientRows: ReportRow[] = [
        ...parsed.errors.map((err) => ({
          key: `parse-${err.line}`,
          label: t("sales.import.lineN", { n: err.line }),
          code:
            err.kind === "price"
              ? "PRICE_INVALID"
              : err.kind === "minQty"
                ? "MIN_QTY_INVALID"
                : "ROW_INVALID",
        })),
        ...assembled.rejected.map((rej) => ({
          key: `client-${rej.line}`,
          label: t("sales.import.lineN", { n: rej.line }),
          code: rej.code,
        })),
      ];

      if (assembled.items.length === 0) {
        onReport({ applied: 0, deleted: 0, rows: clientRows });
        return;
      }
      putItems.mutate(
        { id: listId, body: { items: [...assembled.items] } },
        {
          onSuccess: (serverReport) => {
            onReport({
              applied: serverReport.applied,
              deleted: serverReport.deleted,
              rows: [
                ...clientRows,
                ...serverReport.rejected_rows.map((row) => ({
                  key: `srv-${row.index}`,
                  label: `#${row.index + 1}`,
                  code: row.code,
                })),
              ],
            });
          },
        },
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="sales-import" data-testid="sales-import-panel" title={t("sales.import.title")}>
      <p className="sales-hint">{t("sales.import.hint")}</p>
      <div className="filter-bar">
        <Field label={t("sales.import.file")} wide>
          <input
            type="file"
            accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values"
            data-testid="price-list-import-file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file === undefined) return;
              void file.text().then((text) => run(text));
            }}
          />
        </Field>
      </div>
      <Field label={t("sales.import.paste")} wide className="sales-import-paste">
        <textarea
          rows={4}
          value={pasted}
          placeholder={t("sales.import.pasteHint")}
          data-testid="price-list-import-paste"
          onChange={(e) => {
            setPasted(e.target.value);
          }}
        />
      </Field>
      <div className="sales-toolbar">
        <Button
          size="sm"
          data-testid="price-list-import-run"
          busy={running || putItems.isPending}
          busyLabel={t("sales.import.running")}
          disabled={pasted.trim().length === 0}
          onClick={() => {
            void run(pasted);
          }}
        >
          {t("sales.import.run")}
        </Button>
      </div>
      {note !== undefined ? (
        <p className="sales-error-note" role="alert" data-testid="sales-import-note">
          {note}
        </p>
      ) : null}
      {putItems.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.priceListDetail.saveFailed")}
          error={putItems.error}
        />
      ) : null}
    </Card>
  );
}

/** §4.3's stated activation error state: the conflicting list, inline, so the
 * colliding validity ranges are on one screen. */
function ConflictPanel({
  conflictId,
  own,
  ownTo,
}: {
  conflictId: string;
  own: string;
  ownTo: string | null;
}) {
  const { t, i18n } = useTranslation();
  const conflict = usePriceList(conflictId);
  return (
    <Card
      className="sales-conflict"
      data-testid="sales-price-list-conflict"
      title={t("sales.priceListDetail.conflictTitle")}
    >
      <p>{t("sales.priceListDetail.conflictBody")}</p>
      <p className="num">
        {t("sales.priceListDetail.conflictOwn")}: {formatBusinessDate(own, i18n.language)} –{" "}
        {ownTo === null
          ? t("sales.priceLists.openEnded")
          : formatBusinessDate(ownTo, i18n.language)}
      </p>
      {conflict.isPending ? (
        <p className="sales-hint">{t("sales.common.loading")}</p>
      ) : conflict.data !== undefined ? (
        <p className="num" data-testid="sales-conflict-list">
          <strong>{conflict.data.price_list.code}</strong> · {conflict.data.price_list.name} ·{" "}
          {formatBusinessDate(conflict.data.price_list.valid_from, i18n.language)} –{" "}
          {conflict.data.price_list.valid_to === null
            ? t("sales.priceLists.openEnded")
            : formatBusinessDate(conflict.data.price_list.valid_to, i18n.language)}
        </p>
      ) : (
        <p className="sales-hint">
          <RefLabel value={undefined} id={conflictId} />
        </p>
      )}
    </Card>
  );
}
